import { CheckIcon, CircleUserRoundIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import fs from '@/components/atoms/Fieldset';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useRoles } from '@/hooks/useRoles';
import { Invitation, RespondInvitation } from '@/shared/types/member';
import { getInvitationRoleNames } from '@/util/memberUtils';

interface InvitationFieldsetProps {
  invitation: Invitation;
  respondInvitation: (invitationData: RespondInvitation) => void;
}

const InvitationFieldset = ({ invitation, respondInvitation }: InvitationFieldsetProps) => {
  const { t } = useTranslation();
  const { roles: accountRoles } = useRoles();
  const { account_id, account_name, account_avatar, invitation_code } = invitation;
  const roles = getInvitationRoleNames(invitation, t, accountRoles);

  const roleNames = roles.map((role) => role.name).join(', ');

  return (
    <fs.Fieldset>
      <div className="flex items-center gap-4">
        {account_avatar ? (
          <img
            src={account_avatar}
            alt={account_name}
            referrerPolicy="no-referrer"
            className="size-10 shrink-0 rounded-full"
          />
        ) : (
          <CircleUserRoundIcon className="size-10 shrink-0 text-foreground-muted" />
        )}
        <div className="flex-1">
          <div className="text-sm font-medium text-foreground">
            {t('invitations.you_are_invited', { 0: account_name })}
          </div>
          {roles.length > 0 && (
            <div className="text-sm text-foreground-muted">
              {t('invitations.invitation_subtitle', { 0: roleNames })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <AlertDialog>
            <InfoTooltip content={t('common.reject')}>
              <AlertDialogTrigger asChild>
                <Button variant="outline-neutral" size="icon-md" aria-label={t('common.reject')}>
                  <XIcon className="size-4" />
                </Button>
              </AlertDialogTrigger>
            </InfoTooltip>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('invitations.reject_title')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('invitations.reject_description', { 0: account_name })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    respondInvitation({ invitation_code, account_id, action: 'cancel' })
                  }
                >
                  {t('common.reject')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <InfoTooltip content={t('common.accept')}>
            <Button
              size="icon-md"
              onClick={() => respondInvitation({ invitation_code, account_id, action: 'accept' })}
              aria-label={t('common.accept')}
            >
              <CheckIcon className="size-4" />
            </Button>
          </InfoTooltip>
        </div>
      </div>
    </fs.Fieldset>
  );
};

export default InvitationFieldset;
